/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import {
  Component,
  ContentChild,
  ElementRef,
  HostListener,
  Inject,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SharePanelComponent } from '../share-panel/share-panel.component';
import { BulbSharePanelTranslationProvider } from '../translation-provider';
import { EmailValidationDialogService } from '../../email-validation-dialog/email-validation-dialog.service';
import { AeroDialogService } from '../../../aero/aero-dialog/aero-dialog.service';

@Component({
  selector: 'bulb-share-button',
  templateUrl: './share-button.component.html',
  styleUrls: ['./share-button.component.scss'],
  // We need to define the scope for transloco to pick up the translations
  // when this component is used outside of the share-panel module.
  providers: [BulbSharePanelTranslationProvider],
  standalone: false,
})
export class ShareButtonComponent {
  /**
   * A template reference containing the button that toggles the share panel.
   */
  @ContentChild('toggleButton') toggleButton: TemplateRef<any>;

  /**
   * A reference to the element that triggers the share panel.
   */
  @ContentChild('toggleElRef', { read: ElementRef }) toggleElRef: ElementRef<any>;

  /**
   * Fallback button element when no #toggleButton template is provided.
   * In practice, this is used on the page view, as that button does not
   * follow the aero style guidelines.
   */
  @ContentChild('toggleElRefDefault', { read: ElementRef }) toggleElRefDefault: ElementRef<any>;

  /**
   * The share panel to be displayed.
   */
  @ViewChild('sharePanel') sharePanel: SharePanelComponent;

  @Input() unitId: string;

  @Input() compositeUnit: boolean;

  @Input() showSharePanel: boolean;

  @Input() disabled: boolean = false;

  @Input() parentUnitId: string;

  @Input() disableScroll: boolean = false;

  /**
   * Default Constructor.
   * @param bulbAuthService Auth service to fetch logged-in user.
   * @param emailValidationDialog Dialog service to send email verification mail.
   * @param aeroDialogService The dialog service for displaying dialogs to the user.
   */
  constructor(
    @Inject('BulbAuthService') private bulbAuthService: any,
    private emailValidationDialog: EmailValidationDialogService,
    private aeroDialogService: AeroDialogService,
  ) {
  }

  /**
   * If the logged-in user is a member of an organization, a paid user, or has at least one verified email,
   * the share panel or share limit modal is opened. Otherwise, a dialog is shown to alert the user that they
   * need at least one verified email before publishing the page.
   *
   * @param event - The click event object passed by the HostListener.
   */
  @HostListener('click', ['$event'])
  public toggle(event: Event): void {
    const { currentUser } = this.bulbAuthService;
    if (!this.isVerifiedUser()) {
      this.emailValidationDialog.show(currentUser.defaultEmail, currentUser.id);
    } else if (currentUser.orgSuspended) {
      this.aeroDialogService.showOrgSuspended();
    } else {
      if (this.toggleElRefDefault && !this.toggleElRefDefault.nativeElement.contains(event.target)) return;
      if (this.toggleElRef && !this.toggleElRef.nativeElement.contains(event.target)) return;
      if (this.disabled) return;

      // If free limit has been reached for user then will share limit modal.
      if (this.showSharePanel) {
        this.sharePanel.toggle(event);
      } else {
        window.dispatchEvent(new CustomEvent('showSharedLimitModal'));
      }
    }
  }

  /**
   * Checks whether the current user is a member of an organization or has an active billing status or email verified.
   *
   * @returns boolean - Returns true if the user is part of an organization or if their billing status is 'ACTIVE' or
   * email verified, otherwise false.
   */
  private isVerifiedUser() {
    const { currentUser } = this.bulbAuthService;
    if (currentUser.groupMemberships.some((group) => group.type === 'ORGANIZATION')
      || currentUser.billing.status === 'ACTIVE') {
      return true;
    }
    return currentUser.emails.some((email) => email.verified === true);
  }

  public buttonIcon = 'paper-plane';
}
